<script lang="ts" setup>
import { ref } from 'vue';
import {usePartnerConfig} from "~/stores/usePartnerConfig";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import {useLocaleStore} from "~/stores/useLocaleStore";
import MainLogo from "~/components/MainLogo.vue";
import IconWechat from "~/components/sx/icons/footer/IconWechat.vue";
import {onClickOutside} from "@vueuse/core";
import IconTg from "~/components/sx/icons/footer/IconTg.vue";
import IconMail from "~/components/sx/icons/footer/IconMail.vue";
import { UiLanguages } from '@common/ui-kit';
import IconSxWhatsApp from "~/components/sx/icons/IconSxWhatsApp.vue";

const { t } = useLocale();
const { config, userCountry, telegramLink, supportEmail, qrValue, whatsappLink, getCountryCode } = usePartnerConfig();
const { setLocale, mappedLanguages, currentLanguage } = useLocaleStore();

const wechat = ref<HTMLDivElement | null>(null);
const whatsapp = ref<HTMLDivElement | null>(null);

const visibleCode = ref({
  wechat: false,
  whatsapp: false,
});

const isLangModalVisible = ref(false);

const items = computed(() => {
  return [
    { name: 'Правила пользования', link: config.value?.terms_and_condition, terms: true },
    { name: 'Правила конфиденциальности', link: config.value?.privacy_and_policy, terms: true },
    { name: 'Политика возвратов', link: config.value?.returns_policy, terms: true },
    { name: 'FAQ', link: '/faq' },
  ];
});

const texts = computed(() => {
  return {
    title: `SX.org — это коммерческое название компании Samal Systems. Все услуги предоставляются за пределами страны (${userCountry.value}), и SX.ORG не имеет никаких лицензий и/или не зарегистрирована в стране (${userCountry.value})`,
    subtitle: '"SX.ORG" использует "cookies" для персонализации услуг и повышения удобства пользования сайтом. "Cookies" - это небольшие файлы, содержащие информацию о предыдущих посещениях сайта. Если Вы не хотите использовать "cookies", пожалуйста, измените настройки Вашего браузера',
  }
});

function removeStream(str: string) {
  return str.replace(/\/stream$/, '');
}

const toggleCode = (target: 'wechat' | 'whatsapp') => {
  visibleCode.value.wechat = false;
  visibleCode.value.whatsapp = false;
  visibleCode.value[target] = !visibleCode.value[target];
};

onMounted(async () => {
  await getCountryCode();

  onClickOutside(wechat.value, () => {
    if (wechat.value) {
      visibleCode.value.wechat = false;
    }
  });
  onClickOutside(whatsapp.value, () => {
    if (whatsapp.value) {
      visibleCode.value.whatsapp = false;
    }
  });
});
</script>

<template>
  <footer class="app-footer">
    <div class="app-footer__inner">
      <div class="app-footer__section">
        <div class="app-footer__logo">
          <main-logo sx link :black="false" />
        </div>
        <div class="app-footer__item-list">
          <a
            v-for="(item, index) in items"
            :key="index"
            :href="removeStream(item.link!)"
            :target="'_blank'"
            :rel="'noopener noreferrer'"
            class="app-footer__item"
          >
            {{ t(item.name) }}
          </a>
          <UiLanguages
            v-model:is-show="isLangModalVisible"
            for-header
            v-model="currentLanguage"
            :locales="mappedLanguages"
            @change="setLocale($event)"
            class="app-footer__language"
          />
        </div>
      </div>

      <div class="app-footer__section column">
        <div class="app-footer__text">
          {{ t(texts.title) }}
        </div>
        <div class="app-footer__info">
          <div class="app-footer__text gray mw-880">
            {{ t(texts.subtitle) }}
          </div>
          <div class="app-footer__icons">
            <span style="position: relative" @click="toggleCode('wechat')">
              <icon-wechat class="app-footer__icon" />
              <span ref="wechat" :class="['qr-code', { visible: visibleCode.wechat }]">
              <VueQrcode
                v-if="qrValue"
                :value="qrValue"
                :options="{ width: 132, height: 132 }"
              />
              </span>
            </span>
            <span style="position: relative" @click="toggleCode('whatsapp')">
              <icon-sx-whats-app color="#fff" class="app-footer__icon" />
              <span ref="whatsapp" :class="['qr-code', { visible: visibleCode.whatsapp }]">
              <VueQrcode
                :value="whatsappLink"
                :options="{ width: 132, height: 132 }"
              />
              </span>
            </span>
            <a
              v-if="telegramLink"
              :href="telegramLink"
              target="_blank"
              :rel="'noopener noreferrer'"
              aria-label="Join our Telegram bot"
            >
              <icon-tg color="#fff" class="app-footer__icon" />
            </a>
            <a :href="`mailto:${supportEmail}`" aria-label="Mail us">
              <icon-mail color="#fff" class="app-footer__icon" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="app-footer__video" style="z-index: 1; height: 100%">
      <SxLinesImg v-if="false" />
      <client-only>
        <lazy-sx-video-lines loop footer />
      </client-only>
    </div>
  </footer>
  <UiLanguages
    v-model:is-show="isLangModalVisible"
    is-hidden
    v-model="currentLanguage"
    :locales="mappedLanguages"
    @change="setLocale($event)"
  />
</template>

<style lang="scss" scoped>
.qr-code {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%) scale(0.8);
  transition: 0.3s;
  opacity: 0;
  pointer-events: none;
  &.visible {
    opacity: 1;
    transform: translateX(-30%) scale(1);
    pointer-events: auto;
  }
}

.blur {
  background: linear-gradient(
      180deg,
      rgb(99 99 99 / 5%) 90%,
      rgb(157 29 29 / 3%) 0%
  );
  position: absolute;
  bottom: 0;
  z-index: 1;
  height: 100%;
  width: 100%;
}

.app-footer {
  background: var(--bg-sx-black);
  padding: 4rem 2rem;
  display: flex;
  //align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  font-family: AlsHauss, sans-serif;
  @media (min-width: 680px)  {
    padding: 333px 2rem 35px;
    align-items: flex-start;
    height: 512px;
    //padding: 115px 2rem 35px;
  }

  &__icons {
    display: flex;
    align-items: center;
    gap: 16px
  }
  &__icon {
    background: #222325;
    border-radius: 4px;
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    transition: .3s;
    cursor: pointer;
    &:hover {
      background: var(--bg-sx-black-hover);
    }
  }
  &__inner {
    z-index: 2;
    max-width: 1200px;

    @media (min-width: 480px) {
      align-items: center;
    }
  }
  &__video {
    left: 0;
    top: 0;
    min-width: 100%;
    position: absolute;
    display: none;
    @media (min-width: 480px)  {
      display: block;
    }
  }
  &__language {
    display: flex;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: .3s;
    :deep(button) {
      height: 36px;
      padding: 0 8px;
      &:hover {
        background: #eeeeee;
      }
    }
  }
  &__flag {
    :deep(svg) {
      width: 22px;
      height: 16px;
    }
  }
  &__locale {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--font-sx-color-white);
  }
  &__section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 28px;
    padding-top: 24px;
    gap: 16px;
    &:first-child {
      padding-bottom: 24px;
      padding-top: 0;
      border-bottom: var(--border-sx-contrast);
    }
    &.column {
      flex-direction: column;
      align-items: flex-start;
    }
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__info {
    width: 100%;
    gap: 24px;
    display: flex;
    justify-content: space-between;
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__logo {
    :deep(.logo) {
      max-width: 100px;
      min-width: 100px;
      max-height: 20px;
      img {
        object-fit: cover;
      }
    }
  }
  &__item-list {
    display: flex;
    align-items: center;
    gap: 24px;
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__item {
    color: var(--font-sx-color-white);
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    transition: .3s;
    &:hover {
      text-decoration: underline;
    }
  }
  &__text {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--font-sx-color-white);
    &.gray {
      color: var(--font-sx-color-gray);
    }
    &.mw-880 {
      max-width: 880px;
    }
  }
}
</style>
